import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { resolveAppEndpoints } from './app-endpoints';
import {
  ActionPlan,
  Brand,
  DashboardSummary,
  Diagnostic,
  ExperienceMap,
  ExperienceMapPage,
  HumanRunDetail,
  IntegrationConfig,
  LaunchRunResponse,
  OrganizationCreditSummary,
  Persona,
  RetailAgent,
  RetailAgentCredentials,
  RetailRunDetail,
  RunDetail,
  RunLogsResponse,
  Scenario,
  ScenarioRun,
  SeoPlan,
  WorkspaceContext,
} from './models';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly baseUrl = `${resolveAppEndpoints().apiBaseUrl}/api`;
  readonly runningScenarioIds = new Set<string>();

  constructor(private readonly http: HttpClient) {}

  getContext(): Promise<WorkspaceContext> {
    return firstValueFrom(this.http.get<WorkspaceContext>(`${this.baseUrl}/context`));
  }

  getOrganizationCredits(): Promise<OrganizationCreditSummary> {
    return firstValueFrom(
      this.http.get<OrganizationCreditSummary>(`${this.baseUrl}/organization-credits`),
    );
  }

  getDashboard(): Promise<DashboardSummary> {
    return firstValueFrom(this.http.get<DashboardSummary>(`${this.baseUrl}/dashboard`));
  }

  listBrands(): Promise<Brand[]> {
    return firstValueFrom(this.http.get<Brand[]>(`${this.baseUrl}/brands`));
  }

  createBrand(payload: Record<string, unknown>): Promise<Brand> {
    return firstValueFrom(this.http.post<Brand>(`${this.baseUrl}/brands`, payload));
  }

  getBrand(brandId: string): Promise<Brand> {
    return firstValueFrom(this.http.get<Brand>(`${this.baseUrl}/brands/${brandId}`));
  }

  updateBrand(brandId: string, payload: Record<string, unknown>): Promise<Brand> {
    return firstValueFrom(this.http.put<Brand>(`${this.baseUrl}/brands/${brandId}`, payload));
  }

  deleteBrand(brandId: string): Promise<void> {
    return firstValueFrom(this.http.delete<void>(`${this.baseUrl}/brands/${brandId}`));
  }

  listPersonas(): Promise<Persona[]> {
    return firstValueFrom(this.http.get<Persona[]>(`${this.baseUrl}/personas`));
  }

  getPersona(personaId: string): Promise<Persona> {
    return firstValueFrom(this.http.get<Persona>(`${this.baseUrl}/personas/${personaId}`));
  }

  createPersona(payload: Record<string, unknown>): Promise<Persona> {
    return firstValueFrom(this.http.post<Persona>(`${this.baseUrl}/personas`, payload));
  }

  updatePersona(personaId: string, payload: Record<string, unknown>): Promise<Persona> {
    return firstValueFrom(this.http.put<Persona>(`${this.baseUrl}/personas/${personaId}`, payload));
  }

  archivePersona(personaId: string): Promise<Persona> {
    return firstValueFrom(this.http.delete<Persona>(`${this.baseUrl}/personas/${personaId}`));
  }

  generatePersona(prompt: string, commerceType: 'btoc' | 'btob' = 'btoc'): Promise<Record<string, unknown>> {
    return firstValueFrom(this.http.post<Record<string, unknown>>(`${this.baseUrl}/personas/generate`, { prompt, commerceType }));
  }

  listScenarios(): Promise<Scenario[]> {
    return firstValueFrom(this.http.get<Scenario[]>(`${this.baseUrl}/scenarios`));
  }

  getScenario(scenarioId: string): Promise<Scenario> {
    return firstValueFrom(this.http.get<Scenario>(`${this.baseUrl}/scenarios/${scenarioId}`));
  }

  createScenario(payload: Record<string, unknown>): Promise<Scenario> {
    return firstValueFrom(this.http.post<Scenario>(`${this.baseUrl}/scenarios`, payload));
  }

  updateScenario(scenarioId: string, payload: Record<string, unknown>): Promise<Scenario> {
    return firstValueFrom(this.http.put<Scenario>(`${this.baseUrl}/scenarios/${scenarioId}`, payload));
  }

  deleteScenario(scenarioId: string): Promise<void> {
    return firstValueFrom(this.http.delete<void>(`${this.baseUrl}/scenarios/${scenarioId}`));
  }

  generateScenario(prompt: string, commerceType: 'btoc' | 'btob' = 'btoc'): Promise<Record<string, unknown>> {
    return firstValueFrom(this.http.post<Record<string, unknown>>(`${this.baseUrl}/scenarios/generate`, { prompt, commerceType }));
  }

  listRuns(): Promise<ScenarioRun[]> {
    return firstValueFrom(this.http.get<ScenarioRun[]>(`${this.baseUrl}/runs`));
  }

  listActiveRuns(): Promise<ScenarioRun[]> {
    return firstValueFrom(this.http.get<ScenarioRun[]>(`${this.baseUrl}/runs/active`));
  }

  async getRunningSteps(): Promise<Map<string, number>> {
    const runs = await this.listRuns();
    const map = new Map<string, number>();
    for (const run of runs) {
      if (run.status === 'running' && run.completedSteps != null) {
        map.set(run.scenarioId, run.completedSteps);
      }
    }
    return map;
  }

  async getRunningRunIds(): Promise<Map<string, string>> {
    const runs = await this.listRuns();
    const map = new Map<string, string>();
    for (const run of runs) {
      if (run.status === 'running' || run.status === 'queued') {
        map.set(run.scenarioId, run.id);
      }
    }
    return map;
  }

  getRunLogs(runId: string, since: number): Promise<RunLogsResponse> {
    return firstValueFrom(
      this.http.get<RunLogsResponse>(`${this.baseUrl}/runs/${runId}/logs`, {
        params: { since: String(since) },
      }),
    );
  }

  getRun(runId: string): Promise<RunDetail> {
    return firstValueFrom(this.http.get<RunDetail>(`${this.baseUrl}/runs/${runId}`));
  }

  markRunViewed(runId: string): Promise<void> {
    return firstValueFrom(this.http.post<void>(`${this.baseUrl}/runs/${runId}/viewed`, {}));
  }

  deleteRun(runId: string): Promise<void> {
    return firstValueFrom(this.http.delete<void>(`${this.baseUrl}/runs/${runId}`));
  }

  async launchRun(scenarioId: string, retailCredentials?: RetailAgentCredentials | null): Promise<LaunchRunResponse> {
    this.runningScenarioIds.add(scenarioId);
    try {
      return await firstValueFrom(this.http.post<LaunchRunResponse>(`${this.baseUrl}/scenarios/${scenarioId}/runs`, {
        ...(retailCredentials ? { retailCredentials } : {}),
      }));
    } finally {
      this.runningScenarioIds.delete(scenarioId);
    }
  }

  cancelScenario(scenarioId: string): Promise<{ status: string }> {
    return firstValueFrom(this.http.post<{ status: string }>(`${this.baseUrl}/scenarios/${scenarioId}/cancel`, {}));
  }

  listDiagnostics(): Promise<Diagnostic[]> {
    return firstValueFrom(this.http.get<Diagnostic[]>(`${this.baseUrl}/diagnostics`));
  }

  listActionPlans(): Promise<ActionPlan[]> {
    return firstValueFrom(this.http.get<ActionPlan[]>(`${this.baseUrl}/action-plans`));
  }

  listSeoPlans(): Promise<SeoPlan[]> {
    return firstValueFrom(this.http.get<SeoPlan[]>(`${this.baseUrl}/seo-plans`));
  }

  listIntegrations(): Promise<IntegrationConfig[]> {
    return firstValueFrom(this.http.get<IntegrationConfig[]>(`${this.baseUrl}/integrations`));
  }

  updateIntegrations(configs: IntegrationConfig[]): Promise<IntegrationConfig[]> {
    return firstValueFrom(this.http.put<IntegrationConfig[]>(`${this.baseUrl}/integrations`, { configs }));
  }

  listRetailAgents(): Promise<RetailAgent[]> {
    return firstValueFrom(this.http.get<RetailAgent[]>(`${this.baseUrl}/retail-agents`));
  }

  getRetailRun(runId: string): Promise<RetailRunDetail> {
    return firstValueFrom(this.http.get<RetailRunDetail>(`${this.baseUrl}/runs/${runId}/retail`));
  }

  getHumanRun(runId: string): Promise<HumanRunDetail> {
    return firstValueFrom(this.http.get<HumanRunDetail>(`${this.baseUrl}/runs/${runId}/human`));
  }

  getExperienceMap(runId: string): Promise<ExperienceMap | null> {
    return firstValueFrom(this.http.get<ExperienceMap | null>(`${this.baseUrl}/runs/${runId}/experience-map`));
  }

  generateExperienceMap(runId: string): Promise<ExperienceMap> {
    return firstValueFrom(this.http.post<ExperienceMap>(`${this.baseUrl}/runs/${runId}/experience-map`, {}));
  }

  listExperienceMaps(limit: number, offset: number): Promise<ExperienceMapPage> {
    return firstValueFrom(
      this.http.get<ExperienceMapPage>(`${this.baseUrl}/experience-maps`, {
        params: { limit: String(limit), offset: String(offset) },
      }),
    );
  }

  deleteExperienceMap(runId: string): Promise<void> {
    return firstValueFrom(this.http.delete<void>(`${this.baseUrl}/runs/${runId}/experience-map`));
  }
}
